import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { ROUTES } from '@/constants/constants';
import { Context } from '@/context/appContext';
import { getResponseError } from '@/helpers';
import { logEvent } from '@/helpers/eventLogger';
import { BASE_TARIFF } from '@/helpers/payments';
import { AppStateActions } from '@/reducers/appReducer';
import { cn } from '@/lib/utils';
import { getSubscriptionPlans } from '@editar-pdf/api';
import { RefreshCw, X } from 'lucide-react';
import { useCallback, useContext, useEffect, useId, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BillingSummary } from './BillingSummary';
import { PaymentForm, type PaymentFormStatus } from './PaymentForm';
import { PlanPicker } from './PlanPicker';

const INITIAL_STATUS: PaymentFormStatus = { ready: false, submitting: false, failed: false };

function PlanPickerSkeleton() {
  return (
    <div className="flex flex-col gap-2" data-testid="plan-picker-skeleton">
      {[0, 1, 2].map((row) => (
        <Skeleton key={row} className="h-[50px] rounded-lg" />
      ))}
    </div>
  );
}

/**
 * Shown in place of the dialog body when any of the three setup calls fails — the plans
 * fetch, the Stripe key, or the setup intent. One block and one Retry for all three: they
 * are two GETs and an intent create, and a rare network blip should not need the user to
 * work out which half broke.
 */
function CheckoutErrorState({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('web', { keyPrefix: 'components.paymentModal' });

  return (
    <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="checkout-error">
      <p className="text-h4">{t('errorTitle')}</p>
      <p className="measure-lead text-body-sm text-pretty">{t('errorDescription')}</p>
      <Button variant="outline" onClick={onRetry} className="mt-1">
        <RefreshCw />
        {t('retry')}
      </Button>
    </div>
  );
}

function StripePaymentModal() {
  const { t, i18n } = useTranslation('web', { keyPrefix: 'components.paymentModal' });
  const {
    state: { uploadedFileId, preselectedTariff, subscriptionPlans, userInfo },
    dispatch,
  } = useContext(Context);
  const navigate = useNavigate();

  const [tariff, setTariff] = useState(BASE_TARIFF);
  const [status, setStatus] = useState<PaymentFormStatus>(INITIAL_STATUS);
  const [plansFailed, setPlansFailed] = useState(false);
  // Bumping this re-runs PaymentForm's two Stripe setup calls.
  const [reloadToken, setReloadToken] = useState(0);

  // The submit button lives in the dialog footer, outside the <form> element that Stripe's
  // PaymentElement sits in, so it reaches the form by id rather than by being its descendant.
  // React 18's useId yields ids like `:r3:`; the colons are legal in an id but trip up any
  // querySelector against it, so they come out here.
  const formId = `stripe-payment${useId().replace(/:/g, '')}`;

  // No toast on failure: the dialog swaps its body for CheckoutErrorState, which says the
  // same thing where the user is already looking.
  const loadPlans = useCallback(async () => {
    try {
      const resp = await getSubscriptionPlans();
      setPlansFailed(false);
      dispatch({
        type: AppStateActions.SET_SUBSCRIPTION_PLANS,
        payload: { subscriptionPlans: resp.data },
      });
    } catch (e) {
      logEvent({ logType: 'ERROR', eventType: 'CHECKOUT_ERROR', trace: getResponseError(e) });
      setPlansFailed(true);
    }
  }, [dispatch]);

  // Retrying clears the failure flags, so the body goes straight back to its skeletons —
  // which already read as "loading" — and the error block returns only if it fails again.
  // That leaves one source of truth for "is this broken" instead of a second `retrying` flag.
  const handleRetry = useCallback(() => {
    setPlansFailed(false);
    setReloadToken((token) => token + 1);
    void loadPlans();
  }, [loadPlans]);

  const handleClose = useCallback(() => {
    // Closing unmounts <Elements> and destroys the PaymentElement; refuse while a payment
    // is in flight to avoid Stripe "Element already destroyed" errors.
    if (status.submitting) return;

    dispatch({
      type: AppStateActions.SET_NEED_PAYMENT,
      payload: { needPayment: false, preselectedTariff: undefined },
    });
  }, [dispatch, status.submitting]);

  const handleSubscribed = useCallback(() => {
    dispatch({
      type: AppStateActions.SET_LAST_PAYMENT_DATE,
      payload: { lastPaymentDate: new Date().toLocaleString() },
    });
    if (userInfo) {
      dispatch({
        type: AppStateActions.SET_USER_INFO,
        payload: { userInfo: { ...userInfo, subscriptionType: tariff } },
      });
    }
    dispatch({
      type: AppStateActions.SET_NEED_PAYMENT,
      payload: { needPayment: false, preselectedTariff: undefined },
    });
    if (uploadedFileId && location.pathname !== ROUTES.convertToWord(i18n.language)) {
      navigate(ROUTES.results(i18n.language, uploadedFileId));
    }
  }, [dispatch, i18n.language, navigate, tariff, uploadedFileId, userInfo]);

  useEffect(() => {
    setTariff(preselectedTariff ?? BASE_TARIFF);
  }, [preselectedTariff]);

  useEffect(() => {
    void loadPlans();
  }, [loadPlans]);

  const errored = plansFailed || status.failed;

  return (
    <Dialog open onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        // The close button is ours rather than the primitive's default, because it has to be
        // disabled mid-submit and DialogContent's built-in one takes no disabled state.
        showCloseButton={false}
        className="max-h-[calc(100dvh-2rem)] gap-0 sm:max-w-3xl"
        // Only the close and Cancel buttons may dismiss this dialog: an accidental backdrop
        // click or escape mid-checkout costs a conversion and destroys the PaymentElement.
        onEscapeKeyDown={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            <Trans t={t} i18nKey="title" components={{ accent: <span className="text-primary" /> }} />
          </DialogTitle>
          <DialogDescription className="text-pretty">{t('subtitle')}</DialogDescription>
        </DialogHeader>

        <button
          type="button"
          onClick={handleClose}
          disabled={status.submitting}
          aria-label={t('close')}
          className="absolute top-5 right-5 rounded-md p-1 text-ink-2 transition-colors duration-(--animate-duration-hover) ease-(--ease-standard) hover:bg-canvas hover:text-ink focus-visible:ring-focus disabled:pointer-events-none disabled:text-disabled"
        >
          <X className="size-4" />
        </button>

        <DialogBody className="min-h-0 flex-1 overflow-y-auto">
          {errored && <CheckoutErrorState onRetry={handleRetry} />}

          {/* Hidden rather than unmounted while the error block is up. PaymentForm owns the
              two Stripe setup calls and reports their failure back here, so unmounting it
              would clear `status.failed`, hide the error block, remount, and fail again —
              a loop. `hidden` keeps its state (and any live PaymentElement) intact. */}
          <div className={cn('grid gap-6 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] md:gap-8', errored && 'hidden')}>
            <div className="flex flex-col gap-4">
              {subscriptionPlans ? (
                <PlanPicker
                  tariff={tariff}
                  plans={subscriptionPlans}
                  onTariffChange={setTariff}
                  disabled={status.submitting}
                />
              ) : (
                <PlanPickerSkeleton />
              )}

              <Separator />

              <BillingSummary tariff={tariff} plans={subscriptionPlans} />
            </div>

            <PaymentForm
              formId={formId}
              documentId={uploadedFileId || undefined}
              subscriptionType={tariff}
              reloadToken={reloadToken}
              onSubscriptionSuccess={handleSubscribed}
              onCancel={handleClose}
              onStatusChange={setStatus}
            />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={status.submitting}>
            {t('cancel')}
          </Button>
          {/* `disabled` is explicit about the in-flight case: Button resolves it as
              `disabled ?? loading`, so passing only `!status.ready` would leave the button
              live mid-submit and allow a double charge. */}
          <Button type="submit" form={formId} loading={status.submitting} disabled={!status.ready || status.submitting}>
            {t('subscribe')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export { StripePaymentModal };
